import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const EMPTY_FORM = {
  code: "", name: "", gstin: "", pan: "",
  registered_address: "", contact: "",
};

function validateCompany(f, isEdit) {
  const errors = {};
  if (!f.name?.trim()) errors.name = "Name is required";
  if (!isEdit) {
    if (!f.code?.trim()) errors.code = "Code is required";
    else if (!/^[A-Z0-9-]{2,20}$/.test(f.code.trim()))
      errors.code = "Code: uppercase letters, digits, dashes only (2–20 chars)";
  }
  if (f.gstin && !/^[0-9A-Z]{15}$/.test(f.gstin.trim()))
    errors.gstin = "GSTIN must be 15 uppercase alphanumeric characters";
  if (f.pan && !/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(f.pan.trim()))
    errors.pan = "PAN format: 5 letters, 4 digits, 1 letter";
  return errors;
}

export default function Companies() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [dialog, setDialog] = useState(null); // { mode: 'create' | 'edit', company?: obj }
  const [deleting, setDeleting] = useState(null);
  const [f, setF] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const canManage = user?.role === "admin" || user?.role === "director";
  const canDelete = user?.role === "admin";

  const load = () => api.get("/companies").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openCreate = () => { setDialog({ mode: "create" }); setF(EMPTY_FORM); setErrors({}); };
  const openEdit = (c) => {
    setDialog({ mode: "edit", company: c });
    setF({ ...EMPTY_FORM, ...c });
    setErrors({});
  };

  const submit = async () => {
    const isEdit = dialog?.mode === "edit";
    const err = validateCompany(f, isEdit);
    if (Object.keys(err).length) { setErrors(err); return; }
    setSaving(true);
    try {
      const payload = {
        name: f.name.trim(),
        code: f.code.trim().toUpperCase(),
        gstin: f.gstin?.trim().toUpperCase() || null,
        pan: f.pan?.trim().toUpperCase() || null,
        registered_address: f.registered_address?.trim() || null,
        contact: f.contact?.trim() || null,
      };
      if (isEdit) {
        delete payload.code;
        await api.patch(`/companies/${dialog.company.id}`, payload);
        toast.success(`Updated ${dialog.company.code}`);
      } else {
        await api.post("/companies", payload);
        toast.success(`Created ${payload.code}`);
      }
      setDialog(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/companies/${deleting.id}`);
      toast.success(`${deleting.code} deleted`);
      setDeleting(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setDeleting(null);
    }
  };

  return (
    <div className="p-8 space-y-6" data-testid="companies-root">
      <div className="flex justify-between items-end flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Master</div>
          <h1 className="font-heading text-4xl mt-1 tracking-tight">Companies</h1>
          <p className="text-sm text-zinc-500 mt-1">Legal entities that centres roll up to. Visible to all users.</p>
        </div>
        {canManage && (
          <Button onClick={openCreate} data-testid="new-company-btn"
            className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white h-10 gap-2">
            <Plus size={14} weight="bold" /> New Company
          </Button>
        )}
      </div>

      <div className="bg-white border border-zinc-200 overflow-x-auto" data-testid="companies-table">
        {items.length === 0 ? (
          <div className="p-12 text-center text-sm text-zinc-500" data-testid="companies-empty">
            No companies yet. Create one to start grouping centres.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left p-4">Code</th>
                <th className="text-left p-4">Name</th>
                <th className="text-left p-4">GSTIN</th>
                <th className="text-left p-4">PAN</th>
                <th className="text-left p-4">Contact</th>
                <th className="text-right p-4">Centres</th>
                {canManage && <th className="text-right p-4">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-t border-zinc-100 hover:bg-zinc-50"
                  data-testid={`company-row-${c.code}`}>
                  <td className="p-4 font-mono text-xs">{c.code}</td>
                  <td className="p-4 font-medium">
                    {c.name}
                    {c.registered_address && (
                      <div className="text-xs text-zinc-500 font-normal mt-0.5 max-w-xs truncate">{c.registered_address}</div>
                    )}
                  </td>
                  <td className="p-4 font-mono text-xs text-zinc-600">{c.gstin || "—"}</td>
                  <td className="p-4 font-mono text-xs text-zinc-600">{c.pan || "—"}</td>
                  <td className="p-4 text-zinc-600">{c.contact || "—"}</td>
                  <td className="p-4 text-right tabular-nums" data-testid={`company-centre-count-${c.code}`}>
                    {c.centre_count || 0}
                  </td>
                  {canManage && (
                    <td className="p-4 text-right">
                      <div className="flex justify-end gap-3 items-center">
                        <button onClick={() => openEdit(c)}
                          className="inline-flex items-center gap-1 text-xs text-zinc-600 hover:text-zinc-900"
                          data-testid={`edit-company-${c.code}`}>
                          <Pencil size={12} /> Edit
                        </button>
                        {canDelete && (
                          <button onClick={() => setDeleting(c)}
                            className="inline-flex items-center gap-1 text-xs text-rose-600 hover:text-rose-800"
                            data-testid={`delete-company-${c.code}`}>
                            <Trash size={12} /> Delete
                          </button>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="bg-white rounded-sm max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {dialog?.mode === "edit" ? `Edit Company · ${dialog.company?.code}` : "Create Company"}
            </DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">
              {dialog?.mode === "edit"
                ? "Update company details. Code is immutable to preserve audit history."
                : "Set up a legal entity with statutory identifiers."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Code" error={errors.code} required>
              <Input value={f.code}
                disabled={dialog?.mode === "edit"}
                onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })}
                placeholder="e.g. PARAM-01"
                className="rounded-sm h-10 mt-2" data-testid="company-code" />
            </FormField>
            <FormField label="Name" error={errors.name} required>
              <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
                className="rounded-sm h-10 mt-2" data-testid="company-name" />
            </FormField>
            <FormField label="GSTIN" error={errors.gstin}>
              <Input value={f.gstin} onChange={(e) => setF({ ...f, gstin: e.target.value.toUpperCase() })}
                placeholder="15-character GSTIN"
                className="rounded-sm h-10 mt-2 font-mono" data-testid="company-gstin" />
            </FormField>
            <FormField label="PAN" error={errors.pan}>
              <Input value={f.pan} onChange={(e) => setF({ ...f, pan: e.target.value.toUpperCase() })}
                placeholder="e.g. ABCDE1234F"
                className="rounded-sm h-10 mt-2 font-mono" data-testid="company-pan" />
            </FormField>
            <FormField label="Contact">
              <Input value={f.contact} onChange={(e) => setF({ ...f, contact: e.target.value })}
                placeholder="Phone / email"
                className="rounded-sm h-10 mt-2" data-testid="company-contact" />
            </FormField>
            <div className="col-span-2">
              <FormField label="Registered Address">
                <Input value={f.registered_address}
                  onChange={(e) => setF({ ...f, registered_address: e.target.value })}
                  className="rounded-sm h-10 mt-2" data-testid="company-address" />
              </FormField>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="save-company"
              className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white">
              {saving ? "Saving…" : dialog?.mode === "edit" ? "Save Changes" : "Create Company"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent className="bg-white rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              The company is removed only if no centres are assigned to it. This action is audited.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm" data-testid="delete-company-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="delete-company-confirm"
              className="rounded-sm bg-rose-600 hover:bg-rose-700 text-white">
              Delete Company
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function FormField({ label, error, required, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest">
        {label}
        {required && <span className="text-rose-600 ml-0.5">*</span>}
      </Label>
      {children}
      {error && <p className="text-[10px] text-rose-600 mt-1">{error}</p>}
    </div>
  );
}
